import { Seq } from 'immutable';
import { createSelector } from 'reselect';
import { getPathname } from '../../modules/app/selectors';
import getPricedBetIds from '../../modules/cashInPrices/selectors';
import { OPEN_BETS as OPEN_BETS_PATH } from '../../routes/paths';
import { noScoreboards, supportedLivescores } from './unsupportedSports';

const getOpenBets = state => state.getIn(['openBets', 'entities', 'bets']);
const getSelections = state => state.getIn(['openBets', 'entities', 'selections']);
const getEvents = state => state.getIn(['openBets', 'entities', 'events']);
const getMarkets = state => state.getIn(['openBets', 'entities', 'markets']);

export const getOpenBet = (state, { betId }) => (
  state.getIn(['openBets', 'entities', 'bets', betId])
);
export const getSelection = (state, { selectionUniqueId }) => (
  state.getIn(['openBets', 'entities', 'selections', selectionUniqueId])
);
export const getEvent = (state, { eventId }) => (
  state.getIn(['openBets', 'entities', 'events', eventId])
);

export const getOpenBetsIds = state => state.getIn(['openBets', 'betsIds']);
export const getCurrentBetId = state => state.getIn(['openBets', 'currentBetId']);
export const getCurrentSelectionId = state => state.getIn(['openBets', 'currentSelectionId']);
export const getCashedOutPrices = state => state.getIn(['openBets', 'cashedOutPrices']);
export const getNextSlip = state => state.getIn(['openBets', 'nextSlip']);
export const hasNextSlip = createSelector(
  getNextSlip,
  nextSlip => !!nextSlip
);

export const hasOpenBets = createSelector(
  getOpenBetsIds,
  betsIds => !betsIds.isEmpty()
);

export const countOpenBets = createSelector(
  getOpenBetsIds,
  betsIds => betsIds.size
);

export const getFirstBetId = createSelector(
  getOpenBetsIds,
  betsIds => betsIds.first()
);

export const isCurrentBetIdValid = createSelector(
  getOpenBetsIds,
  getCurrentBetId,
  (betsIds, currentBetId) => betsIds.includes(currentBetId)
);

export const getCurrentBet = createSelector(
  getOpenBets,
  getCurrentBetId,
  (bets, currentBetId) => bets.get(currentBetId)
);

export const getSingleSelectionLabel = createSelector(
  getOpenBet,
  getSelections,
  (openBet, selections) => {
    if (openBet.betType === 'Single') {
      const firstSelectionId = openBet.get('selections').first();
      return selections.getIn([firstSelectionId, 'selectionLabel']);
    }
    return null;
  }
);

export const getCurrentBetFundingType = createSelector(
  getCurrentBet,
  bet => bet.fundingType
);

const formatFields = bet => bet.merge({
  betStakePerLine: `£${bet.betStakePerLine}`,
  betStake: `£${bet.betStake}`,
  betPotentialPayout: `£${bet.betPotentialPayout}`,
  betCashInPrice: `£${bet.betCashInPrice}`
});

export const getFormattedCurrentBet = createSelector(
  getCurrentBet,
  bet => formatFields(bet)
);

export const getCashInStatus = createSelector(
  getCurrentBet,
  (bet) => {
    if (bet) {
      const cashInAvailable = bet.betCashOutStatus === 'Available';
      const cashInSuspended = bet.betCashOutStatus === 'Suspended';
      const cashInValue = bet.betCashInPrice;
      const cashOutPending = bet.betStatus === 'cashoutPending';
      const cashInValueCapped = cashInValue >= 50000;

      return {
        cashInAvailable,
        cashInSuspended,
        cashInValue,
        cashOutPending,
        cashInValueCapped
      };
    }

    return {};
  }
);

export const getCurrentBetCashedOutPrice = createSelector(
  getCurrentBetId,
  getCashedOutPrices,
  (betId, cashedOutPrices) => cashedOutPrices.get(betId)
);

export const makeGetSelectionData = () => createSelector(
  getSelection,
  getCurrentSelectionId,
  getEvents,
  getMarkets,
  (selection, currentSelectionId, events, markets) => {
    const selectionExpanded = currentSelectionId === selection.selectionUniqueId;
    const { eventInPlay, sportName } = events.get(selection.eventId);
    const { marketName } = markets.get(selection.marketId);

    const api = api_config.scoreboards_api;
    const target = api_config.scoreboards_target;
    const params = `lang=en-gb&showSuggestions=false&showStreaming=false&target=${target}`;
    const scoreboardUrl = `${api}/scoreboards/events/${selection.eventId}/launch?${params}`;

    return {
      selectionUniqueId: selection.selectionUniqueId,
      selectionLabel: selection.selectionLabel,
      selectionResult: selection.selectionResult,
      selectionExpanded,
      eventId: selection.eventId,
      eventInPlay,
      marketName,
      scoreboardUrl,
      sport: sportName.toLowerCase().replace(' ', '-')
    };
  }
);

export const getPDSTopicParams = createSelector(
  getCurrentBet,
  getSelections,
  getMarkets,
  getEvents,
  (bet, selections, markets, events) => new Seq(bet.selections)
    .map(id => {
      const selection = selections.get(id);
      const market = markets.get(selection.marketId);
      const event = events.get(selection.eventId);
      return { selection, market, event };
    })
    .filter(({ event }) => {
      const sport = event.sportName.toLowerCase().replace(' ', '-');
      const hasLiveEvents = supportedLivescores.includes(sport) || !noScoreboards.includes(sport);
      return event.eventStartDateTime.includes('Today ') && hasLiveEvents;
    })
    .flatMap(({ selection, market }) => [
      {
        event: selection.eventId
      },
      {
        event: selection.eventId,
        market: selection.marketId
      },
      {
        event: selection.eventId,
        market: selection.marketId,
        selection: selection.selectionId
      }
    ].concat(
      (market.marketLiveId && selection.selectionLiveId)
        ? [
          {
            event: selection.eventId,
            market: market.marketLiveId
          },
          {
            event: selection.eventId,
            market: market.marketLiveId,
            selection: selection.selectionLiveId
          }
        ] : []
    ))
);

export const getLiveEvents = createSelector(
  getCurrentBet,
  getEvents,
  (bet, events) => new Seq(bet.events.toSet())
    .map(eventId => events.get(eventId))
    .filter(event => event.eventInPlay)
    .map(event => ({
      sport: event.sportName.toLowerCase().replace(' ', '-'),
      eventId: event.eventId,
      liveClock: true
    }))
    .filter(event => supportedLivescores.includes(event.sport))
    .toArray()
);

export const getCurrentBetIndex = createSelector(
  getOpenBetsIds,
  getCurrentBetId,
  (betsIds, currentBetId) => betsIds.indexOf(currentBetId) + 1
);

export const hasPreviousBet = createSelector(
  getCurrentBetIndex,
  (currentBetIndex) => currentBetIndex > 1
);

export const hasNextBet = createSelector(
  countOpenBets,
  getCurrentBetIndex,
  (betsCount, currentBetIndex) => currentBetIndex < betsCount
);

export const getListVisible = state => state.getIn(['openBets', 'showList']);
export const isViewLoading = state => state.getIn(['openBets', 'viewIsLoading']);
export const isLoadingMore = state => state.getIn(['openBets', 'loadingMore']);

export const isCashInPossible = createSelector(
  getPathname,
  getCurrentBetId,
  getListVisible,
  getCashedOutPrices,
  getPricedBetIds,
  (path, currentBetId, listVisible, cashedOutPrices, pricedBetIds) => {
    const isSingleBetView = path === OPEN_BETS_PATH && !!currentBetId && !listVisible;
    const cashInPricesInitialized = pricedBetIds.includes(currentBetId);
    const cashedOutPrice = cashedOutPrices.has(currentBetId);

    return isSingleBetView && cashInPricesInitialized && !cashedOutPrice;
  }
);

export const hasLivescoresOrScoreboards = createSelector(
  getCurrentBet,
  getEvents,
  (bet, events) => new Seq(events)
    .filter(event => bet.events.includes(event.eventId))
    .map(event => ({
      eventId: event.eventId,
      sportName: event.sportName.toLowerCase().replace(' ', '-')
    }))
    .some(
      event => supportedLivescores.includes(event.sportName)
        || !noScoreboards.includes(event.sportName)
    )
);
